import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import DynamicMetaTags from '../components/DynamicMetaTags';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState('');

  useEffect(() => {
    // Fetch public settings to get the login background URL
    const fetchBackgroundUrl = async () => {
      try {
        // In development, construct the backend URL; in production, use relative URL
        const backendUrl = '/api';
        const response = await fetch(`${backendUrl}/settings/public`);
        const data = await response.json();
        if (data.login_background_url) {
          setBackgroundUrl(data.login_background_url);
        }
      } catch (error) {
        console.error('Failed to fetch background URL:', error);
      }
    };
    fetchBackgroundUrl();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success(t('messages.success.loginSuccess'));
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DynamicMetaTags />
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundImage: backgroundUrl ? `url('${backgroundUrl}')` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      <Card className="w-full max-w-md relative z-10 shadow-2xl" data-testid="login-card">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_sitetracker-4/artifacts/rf11ftvs_logo_3D_02%20small.png" 
              alt="Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-3xl font-black">{t('auth.welcomeBack')}</CardTitle>
          <CardDescription>{t('auth.loginTitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="engineer@site.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-12"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 font-bold"
              disabled={loading}
              data-testid="login-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('auth.loginButton')
              )}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>{t('auth.noAccount')}</p>
          </div>
        </CardContent>
      </Card>
      </div>
    </>
  );
};

export default Login;